import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, FileText, Eye, ScanLine } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { ScreenHead, ListCard, Empty, Skeletons, Avatar, titleCase, providerShort, useProviders, useClients, DqMarker } from "./psPrimitives";
import { useClientProviders } from "./psMasters";
import { TovSelect, TovStyle } from "./TovSlot";
import { tovSlug, tovSlots, providerClientId, DEFAULT_OUTLINE } from "@/lib/tov";
import { ConfirmSheet } from "./ProvidersScreen";
import { VisitTypeEditor, SectionRows } from "./VisitTypeEditor";
import { STRUCT, secDiff, useVisitTypes, createOverride, revertOverride, saveOverride, seedDemoOverride } from "@/lib/visitTypesData";
import { FormatSpecView } from "./FormatSpecView";

export function NoteTemplateScreen({ mode, onOpenDq, onGoStep }) {
  const { data: providers = [], isLoading } = useClientProviders();
  const [sub, setSub] = useState(null);
  useEffect(() => { if (mode === "full" && providers[0]?.npi_number) seedDemoOverride(providers[0].npi_number); }, [mode, providers]);
  if (isLoading) return <><ScreenHead eyebrow="Encounter Notes" title="Note template" /><Skeletons /></>;
  const current = providers.find((p) => p.npi_number === sub);
  if (current) return <NotePage provider={current} onBack={() => setSub(null)} mode={mode} onOpenDq={onOpenDq} onGoStep={onGoStep} />;

  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Encounter Notes · Optional" title="Note template"
        lede="Teach revQ each provider's note style, then set their exam defaults." />
      {providers.length === 0 ? <Empty>Add a provider first.</Empty> : (
        <ListCard testid="ps-note-list">
          {providers.map((p, i) => {
            const sp = p.soap_profile || {};
            const learned = sp.format_sample?.filename;
            const exc = sp.ros_pe?.exception_based;
            const nSlots = Object.keys(sp.tov_formats || {}).length;
            return (
              <button key={p.id} onClick={() => setSub(p.npi_number)} data-testid={`ps-note-row-${p.npi_number}`}
                className={`w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-black/[.03] transition ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <Avatar text={p.provider_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{p.provider_name}</div>
                  <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">
                    {learned ? `Lens built this template from ${learned}${exc ? " · charting by exception on" : ""}` : "Default SOAP format"}
                    {nSlots ? ` · Default + ${nSlots} visit type${nSlots > 1 ? "s" : ""}` : ""}
                  </div>
                </div>
                {learned || nSlots ? <span className="text-[11px] font-semibold text-[var(--ink2)] flex-none" data-testid={`ps-note-custom-${p.npi_number}`}>Custom</span> : null}
                <ChevronRight size={16} className="text-[var(--gray3)]" />
              </button>
            );
          })}
        </ListCard>
      )}
    </div>
  );
}

function NotePage({ provider, onBack, mode, onOpenDq, onGoStep }) {
  const qc = useQueryClient();
  const npi = provider.npi_number;
  const sp = provider.soap_profile || {};
  const [tab, setTab] = useState("style");
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [vtSel, setVtSel] = useState("");
  const [editVt, setEditVt] = useState(null);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [examDraft, setExamDraft] = useState(null);
  const fileRef = useRef(null);
  const full = mode === "full";
  const { vts } = useVisitTypes();
  const vt = full && vtSel ? vts.find((x) => x.id === vtSel) : null;
  const ov = vt ? vt.overrides[npi] : null;
  // API state: per-visit-type slots (soap_profile.tov_formats[slug]); "" = Default slot
  const { data: clients = [] } = useClients();
  const { data: allProviders = [] } = useClientProviders();
  const cid = providerClientId(provider);
  const names = full ? [] : (clients.find((c) => String(c.client_id) === cid)?.tov_list || []);
  const slots = tovSlots(provider);
  const [tovKey, setTovKey] = useState("");
  const slot = tovKey ? slots[tovKey] : null;
  const curName = tovKey ? (names.find((n) => tovSlug(n) === tovKey) || slot?.label || tovKey) : "";
  const ownTov = !!(slot && (slot.format_spec || slot.format_sample));
  const tovQ = tovKey ? `?tov=${encodeURIComponent(curName)}` : "";

  const refresh = () => qc.invalidateQueries({ queryKey: ["providers"] });
  useEffect(() => { setExamDraft(null); }, [tovKey]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/api/edi/note-to-claim/providers/${npi}/soap-profile/sample${tovQ}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
    } catch (e) { toast.error(describeError(e)); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/api/edi/note-to-claim/providers/${npi}/soap-profile`); refresh(); setConfirmRemove(false); }
    catch (e) { toast.error(describeError(e)); }
    setBusy(false);
  };

  const fs = sp.format_spec, sample = sp.format_sample, rospe = sp.ros_pe;
  const short = providerShort(provider.provider_name);
  const examCount = Object.keys(rospe?.ros || {}).length + Object.keys(rospe?.pe || {}).length;
  const nSlots = Object.keys(slots).length;
  const defaultIsCustom = !!(sample?.filename || fs);
  const styleStatus = vt ? (ov ? `Own layout for ${vt.name}` : `${vt.name} structure · your style`)
    : tovKey ? (ownTov ? `Own template for ${curName}` : defaultIsCustom ? "Uses the Default template" : "Uses the built-in format")
    : (sample?.filename ? `Learned from ${sample.filename}` : "Default SOAP format");
  const tiles = [
    { id: "style", icon: FileText, label: "Note style", status: styleStatus },
    { id: "exam", icon: ScanLine, label: "Exam defaults", status: `Review of systems and physical exam · ${examCount} systems · charting by exception ${rospe?.exception_based ? "on" : "off"}` },
    { id: "preview", icon: Eye, label: "Preview", status: "See a note written with this style and these defaults" },
  ];

  if (editVt) return <VisitTypeEditor vtId={editVt} onBack={() => setEditVt(null)} onOpenDq={onOpenDq} />;

  return (
    <div className="fade-up">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] mb-3"><ChevronLeft size={14} /> All providers</button>
      <ScreenHead eyebrow="Encounter Notes · Optional" title={`${full ? "Note templates" : "Note template"} — ${titleCase(provider.provider_name)}`} />

      {full ? (
        <div className="flex items-center gap-2.5 mb-4" data-testid="note-vt-select">
          <label className="text-[11px] font-semibold text-[var(--gray2)]">Visit type</label>
          <select value={vtSel} onChange={(e) => { setVtSel(e.target.value); setTab("style"); }} className="rounded-[9px] border border-black/[.14] bg-white px-2.5 py-1.5 text-[12.5px] text-[var(--ink)]">
            <option value="">Default — every visit</option>
            {vts.filter((x) => x.note && x.status === "Enabled").map((x) => <option key={x.id} value={x.id}>{x.name}{x.overrides[npi] ? " · overridden" : ""}</option>)}
          </select>
          <DqMarker n={60} onOpen={onOpenDq} />
        </div>
      ) : null}

      {!full ? (
        <TovSelect names={names} slots={slots} value={tovKey} onChange={(k) => { setTovKey(k); setTab("style"); }} onSetup={() => onGoStep?.("visittypes")} />
      ) : null}

      <div className="grid grid-cols-3 gap-2 mb-5">
        {tiles.map((t) => {
          const on = tab === t.id, Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ps-note-tile-${t.id}`}
              className={`text-left rounded-[14px] border p-3.5 transition ${on ? "border-[#5856D6] bg-white" : "border-[#E9E9EC] bg-white hover:bg-black/[.02]"}`}
              style={on ? { boxShadow: "0 0 0 3px rgba(88,86,214,.14)" } : undefined}>
              <Icon size={16} className={on ? "text-[#5856D6]" : "text-[var(--gray2)]"} />
              <div className="text-[12.5px] font-semibold text-[var(--ink)] mt-2">{t.label}</div>
              <div className="text-[10px] text-[var(--gray2)] mt-0.5 leading-snug">{t.status}</div>
            </button>
          );
        })}
      </div>

      {tab === "style" && vt && (
        <VisitTypeStructure vt={vt} ov={ov} npi={npi} short={short} onEdit={() => setEditVt(vt.id)}
          onOverride={() => createOverride(vt.id, npi)} onRevert={() => setConfirmRevert(true)} />
      )}
      {tab === "style" && tovKey && (
        <TovStyle busy={busy} provider={provider} allProviders={allProviders} tovKey={tovKey} curName={curName} slot={slot} own={ownTov}
          defaultFs={fs} defaultIsCustom={defaultIsCustom} short={short} onPick={() => fileRef.current?.click()} onChanged={refresh} />
      )}
      {tab === "style" && !vt && !tovKey && (
        <NoteStyle busy={busy} fs={fs} sample={sample} short={short} onPick={() => fileRef.current?.click()} onRemove={() => setConfirmRemove(true)} />
      )}
      {tab === "exam" && <ExamDefaults key={tovKey} npi={npi} tov={curName} mode={mode} onDone={refresh} onOpenDq={onOpenDq} onDraftChange={setExamDraft} />}
      {tab === "preview" && <Preview key={tovKey} npi={npi} tov={curName} own={ownTov} short={short} mode={mode} onOpenDq={onOpenDq} examDraft={examDraft} />}

      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="ps-note-file" onChange={(e) => upload(e.target.files?.[0])} />
      {confirmRemove ? (
        <ConfirmSheet title="Remove this template?" body={<>Notes go back to the default SOAP format. This also removes their exam defaults and the charting-by-exception attestation.{nSlots ? <span data-testid="ps-remove-cascade-warn"> It may also remove {short}'s {nSlots} visit-type template{nSlots > 1 ? "s" : ""}.</span> : null}</>}
          confirmLabel="Remove template" busy={busy} onConfirm={remove} onCancel={() => setConfirmRemove(false)} />
      ) : null}
      {confirmRevert && vt ? (
        <ConfirmSheet title={`Revert to the ${vt.name} structure?`} body={`This removes only ${short}'s own copy of these sections. Their note style is unaffected.`}
          confirmLabel="Revert" onConfirm={() => { revertOverride(vt.id, npi); setConfirmRevert(false); }} onCancel={() => setConfirmRevert(false)} />
      ) : null}
    </div>
  );
}

function VisitTypeStructure({ vt, ov, npi, short, onEdit, onOverride, onRevert }) {
  if (!ov) {
    return (
      <div data-testid="note-vt-structure">
        <div className="flex items-center gap-3 flex-wrap rounded-[12px] bg-[rgba(88,86,214,.05)] px-4 py-3 mb-4">
          <span className="flex-1 text-[11.5px] text-[var(--ink2)] min-w-[240px]">Structure comes from the <b>{vt.name}</b> visit type ({STRUCT[vt.struct][0]}) and is written in {short}'s style.</span>
          <button onClick={onEdit} className="text-[12px] font-semibold" style={{ color: "#5856D6" }} data-testid="note-vt-edit">Edit visit type ›</button>
          <button onClick={onOverride} className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC] text-[var(--ink)]" data-testid="note-vt-override">Override for {short}</button>
        </div>
        <SectionRows list={vt.secs} editable={false} showMap={false} onChange={() => {}} />
      </div>
    );
  }
  const changes = secDiff(vt.secs, ov);
  return (
    <div data-testid="note-vt-override">
      <div className="flex items-center gap-3 flex-wrap rounded-[12px] bg-[rgba(88,86,214,.05)] px-4 py-3 mb-4">
        <span className="text-[10px] font-bold uppercase px-2 py-[3px] rounded-full flex-none" style={{ color: "#4A4AC2", border: "1px solid rgba(88,86,214,.35)" }}>Override</span>
        <span className="flex-1 text-[11.5px] text-[var(--ink2)] min-w-[200px]">{short} has an own layout for {vt.name} · {changes} change{changes === 1 ? "" : "s"} from the visit type.</span>
        <button onClick={onEdit} className="text-[12px] font-semibold" style={{ color: "#5856D6" }}>Edit visit type ›</button>
        <button onClick={onRevert} className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC] text-[var(--ink)]" data-testid="note-vt-revert">Revert to visit type structure</button>
      </div>
      <SectionRows list={ov} editable showMap onChange={(next) => saveOverride(vt.id, npi, next)} />
    </div>
  );
}

function NoteStyle({ busy, fs, sample, short, onPick, onRemove }) {
  if (busy) return (
    <div className="rounded-[16px] border border-[#E9E9EC] bg-white p-8 text-center">
      <div className="text-[13px] font-semibold text-[var(--ink)]">Lens is reading your note…</div>
      <div className="text-[11.5px] text-[var(--gray2)] mt-1">Matching its headings, order and layout to a template.</div>
      <div className="mt-4 h-1.5 rounded-full overflow-hidden bg-black/[.06]"><div className="h-full w-1/3 rounded-full animate-pulse" style={{ background: "#5856D6" }} /></div>
    </div>
  );
  if (!sample?.filename) {
    return (
      <div data-testid="ps-note-empty">
        <div className="text-[11.5px] text-[var(--gray1)] mb-3">Notes use the default SOAP layout until you upload one of {short}'s past notes via Lens below.</div>
        <div className="rounded-[16px] border-2 border-dashed border-black/[.14] bg-white/40 p-8 text-center">
          <ScanLine size={22} className="mx-auto text-[var(--gray2)]" />
          <div className="text-[13px] font-semibold text-[var(--ink)] mt-3">Upload a past note via Lens</div>
          <div className="text-[11.5px] text-[var(--gray2)] mt-1 max-w-[440px] mx-auto leading-relaxed">Lens reads one of {short}'s notes and auto-matches it to a template — headings, order and layout. Never the patient details.</div>
          <button onClick={onPick} data-testid="ps-note-upload" className="btn-ink inline-flex items-center gap-1.5 mt-4"><ScanLine size={14} /> Upload via Lens…</button>
          <div className="text-[10px] text-[var(--gray3)] mt-2">de-identified PDF · until then, notes use the layout below</div>
        </div>
        <div className="mt-4 rounded-[16px] border border-[#E9E9EC] bg-white p-5 opacity-55">
          {DEFAULT_OUTLINE.map(([h, layout]) => (
            <div key={h} className="mb-3 last:mb-0">
              <div className="text-[12.5px] font-bold text-[var(--ink)]">{h}</div>
              <div className="text-[10px] text-[var(--gray3)] mt-0.5">{layout} · <span className="italic">from the visit</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div data-testid="ps-note-learned">
      <div className="rounded-[12px] bg-[var(--ink)] text-white px-4 py-2.5 text-[11px] flex items-center gap-3 mb-4">
        <ScanLine size={14} />
        <span className="flex-1">Lens built this template from <strong>{sample.filename}</strong>. Headings, order and layout only — never patient details.</span>
        <button onClick={onPick} className="text-[10.5px] font-semibold underline">Replace sample…</button>
        <button onClick={onRemove} className="text-[10.5px] font-semibold underline opacity-80">Remove</button>
      </div>
      <FormatSpecView fs={fs} />
    </div>
  );
}

function ExamDefaults({ npi, tov, mode, onDone, onOpenDq, onDraftChange }) {
  // DQ-59: exam defaults are read back from the dedicated GET (symmetric with the
  // PATCH), not from soap_profile.ros_pe. Baselines are editable; Save is gated on
  // the attestation and the 400 from the server is still handled defensively.
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);   // { eff, stored, source, attestation }
  const [on, setOn] = useState(false);
  const [attested, setAttested] = useState(false);
  const [ros, setRos] = useState({});
  const [pe, setPe] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const q = tov ? `?tov=${encodeURIComponent(tov)}` : "";
      const { data: d } = await api.get(`/api/edi/note-to-claim/providers/${npi}/soap-profile/exam${q}`);
      const def = (tov ? d?.selected : d?.default) || {};
      const eff = def.effective || {};
      setMeta({ eff, stored: def.stored, source: def.source, attestation: def.attestation });
      setOn(!!eff.exception_based);
      // a visit type's first own copy starts unattested — attestation is per slot
      setAttested(tov && def.source !== "tov" ? false : !!eff.attested);
      setRos(eff.ros || {});
      setPe(eff.pe || {});
      setDirty(false);
    } catch (e) { toast.error(describeError(e)); }
    setLoading(false);
  }, [npi, tov]);
  useEffect(() => { load(); }, [load]);

  // Keep Preview in sync with the live (possibly unsaved) exam config.
  useEffect(() => { onDraftChange?.({ exception_based: on, ros, pe, tov: tov || null }); }, [on, ros, pe, tov, onDraftChange]);

  const eff = meta?.eff || {};
  const baseOn = !!eff.exception_based, baseAtt = !!eff.attested;
  const changed = dirty || on !== baseOn || attested !== baseAtt;
  const canSave = changed && (!on || attested);   // enabling exception-based requires attestation

  const editLine = (which, sys, val) => {
    (which === "ros" ? setRos : setPe)((o) => ({ ...o, [sys]: val }));
    setDirty(true);
  };
  const toggleAttest = () => { const v = !attested; setAttested(v); if (!v) setOn(false); };
  const toggleOn = () => { if (!attested) return; setOn((o) => !o); };
  const seedBaseline = async () => {
    try {
      const { data } = await api.get(`/api/edi/note-to-claim/soap-exam-defaults`);
      setRos((o) => ({ ...(data.ros || {}), ...o }));
      setPe((o) => ({ ...(data.pe || {}), ...o }));
      setDirty(true);
    } catch (e) { toast.error(describeError(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/edi/note-to-claim/providers/${npi}/soap-profile/exam`, { exception_based: on, attested, ros, pe, ...(tov ? { tov } : {}) });
      toast.success("Exam defaults saved");
      onDone?.(); await load();
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 400 ? "Enabling charting-by-exception needs the attestation." : describeError(e));
    }
    setBusy(false);
  };

  if (loading) return <div data-testid="ps-exam"><Skeletons /></div>;

  const SOURCE_LABEL = { tov: "Visit type", provider_default: "Provider default", builtin: "Built-in" };
  const srcLabel = SOURCE_LABEL[meta?.source] || "Built-in";
  const attestedBy = meta?.attestation?.attested_by;
  const attestedAt = meta?.attestation?.attested_at;
  const sections = [["Review of systems", ros, "ros"], ["Physical exam", pe, "pe"]];
  const hasAny = Object.keys(ros).length || Object.keys(pe).length;
  return (
    <div data-testid="ps-exam">
      {tov ? (
        <div className="rounded-[12px] bg-[rgba(88,86,214,.05)] px-4 py-2.5 mb-3 text-[11.5px] text-[var(--ink2)]" data-testid="ps-exam-tov-line">
          Exam defaults for <b className="text-[var(--ink)]">{tov}</b>: {meta?.source === "tov" ? "set for this visit type, with their own attestation"
            : meta?.source === "provider_default" ? "inherited from the Default. Editing creates this visit type's own copy, which needs its own attestation."
            : "the built-in baseline."}
        </div>
      ) : null}
      <div className="flex items-center gap-2 mb-3" data-testid="ps-exam-source">
        <span className="text-[10px] font-bold tracking-[.08em] uppercase text-[var(--gray2)]">Showing</span>
        <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-black/[.05] text-[var(--ink2)]">{srcLabel}</span>
        {meta?.stored == null && <span className="text-[10.5px] text-[var(--gray2)]">— not yet saved for this provider; editing seeds from the {srcLabel.toLowerCase()} baseline</span>}
      </div>
      <div className="rounded-[14px] border border-[#E9E9EC] bg-white p-4 mb-4">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={attested} onChange={toggleAttest} disabled={busy} data-testid="ps-exam-attest" className="mt-0.5" />
          <span className="text-[12.5px] text-[var(--ink)]"><b className="font-semibold">I attest</b> these baselines reflect my standard exam and may be used to pre-fill notes.{mode === "full" ? <> <button onClick={() => onOpenDq?.(20)} className="text-[#4646b8] font-semibold">DQ-20</button></> : null}</span>
        </label>
        {attested ? <div className="mt-1.5 ml-6 text-[10.5px] text-[var(--gray2)]">Attested by {attestedBy || "you"}{attestedAt ? ` · ${new Date(attestedAt).toLocaleDateString()}` : ""}</div> : null}

        <div className="mt-3 pt-3 border-t border-[#F0F0F2]">
          <label className={`flex items-center gap-2.5 ${attested ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
            <input type="checkbox" checked={on} onChange={toggleOn} disabled={busy || !attested} data-testid="ps-exam-switch" />
            <span className="text-[12.5px] font-semibold text-[var(--ink)]">Chart normal unless dictated abnormal</span>
          </label>
          <p className="text-[11px] text-[var(--gray2)] mt-1.5 ml-6">{attested ? "ROS/PE start from the baselines below; only dictated abnormals replace a line." : "Attest above to enable charting by exception."}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4" data-testid="ps-exam-savebar">
        <button onClick={save} disabled={!canSave || busy} data-testid="ps-exam-save"
          className="btn-ink inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save exam defaults
        </button>
        {on && !attested ? <span className="text-[11px] font-semibold" style={{ color: "#9A5A00" }}>Attest to enable saving</span>
          : changed ? <span className="text-[11px] text-[var(--gray2)]">Unsaved changes</span>
          : <span className="text-[11px] text-[var(--gray3)]">All changes saved</span>}
        <button onClick={seedBaseline} disabled={busy} data-testid="ps-exam-seed" className="text-[11px] font-semibold text-[#4646b8] ml-auto">Load generalist baseline</button>
      </div>

      {sections.map(([label, obj, which]) => Object.keys(obj).length ? (
        <div key={label} className="mb-4">
          <div className="text-[10px] font-bold tracking-[.1em] uppercase text-[var(--gray2)] mb-2">{label} · {Object.keys(obj).length}</div>
          <ListCard>
            {Object.entries(obj).map(([sys, val], i) => (
              <div key={sys} className={`px-4 py-2.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <div className="text-[11px] font-semibold text-[var(--ink2)] mb-1">{sys}</div>
                <textarea value={val} onChange={(e) => editLine(which, sys, e.target.value)} rows={2} disabled={busy}
                  className="lf-input w-full resize-y text-[11.5px]" data-testid={`ps-exam-${which}-${sys.replace(/\s+/g, "-")}`} />
              </div>
            ))}
          </ListCard>
        </div>
      ) : null)}
      {!hasAny && (
        <Empty>No ROS/PE baselines yet. Click "Load generalist baseline" to seed them, then edit, attest and save.</Empty>
      )}
    </div>
  );
}

function Preview({ npi, tov, own, short, mode, onOpenDq, examDraft }) {
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);      // { soap, soap_text, formatted_note }
  const [view, setView] = useState("note");  // note | structured
  const [visits, setVisits] = useState(null);
  const [picking, setPicking] = useState(false);
  const [loadingTx, setLoadingTx] = useState(false);

  const loadVisits = async () => {
    setPicking((p) => !p);
    if (visits) return;
    try {
      const { data } = await api.get(`/api/edi/note-to-claim/providers/${npi}/sample-visits?limit=25`);
      setVisits(data.visits || []);
    } catch (e) { toast.error(describeError(e)); setVisits([]); }
  };
  const pickVisit = async (v) => {
    setPicking(false); setLoadingTx(true);
    try {
      const { data } = await api.get(`/api/edi/note-to-claim/providers/${npi}/sample-visits/${v.kind}/${v.id}/transcript`);
      setTx(data.transcript || "");
    } catch (e) { toast.error(describeError(e)); }
    setLoadingTx(false);
  };
  const gen = async () => {
    setBusy(true);
    const body = {
      ...(tx.trim() ? { transcript: tx.trim() } : {}),
      ...(tov ? { tov } : {}),
      ...(examDraft ? { exam: { exception_based: !!examDraft.exception_based, ros: examDraft.ros || {}, pe: examDraft.pe || {} } } : {}),
    };
    try { const r = await api.post(`/api/edi/note-to-claim/providers/${npi}/soap-profile/preview`, body); setOut(r.data || null); setView("note"); }
    catch (e) { toast.error(describeError(e)); }
    setBusy(false);
  };

  const formatted = out?.formatted_note || out?.soap_text || "";
  const structured = out?.soap && typeof out.soap === "object" ? out.soap : null;
  return (
    <div data-testid="ps-preview">
      {tov ? (
        <div className="rounded-[12px] bg-[rgba(88,86,214,.05)] px-4 py-2.5 mb-3 text-[11.5px] text-[var(--ink2)]" data-testid="ps-preview-tov-line">
          Previewing {short}'s <b className="text-[var(--ink)]">{tov}</b> note{own ? "" : " (uses the Default template)"}
        </div>
      ) : null}
      {examDraft?.exception_based ? (
        <div className="text-[10.5px] text-[var(--gray2)] mb-2" data-testid="ps-preview-exam-note">Using your current exam defaults (unsaved edits included).</div>
      ) : null}

      <div className="relative mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold tracking-[.1em] uppercase text-[var(--gray2)]">Transcript</span>
          <button onClick={loadVisits} data-testid="ps-preview-pick-visit" className="text-[11px] font-semibold text-[#4646b8]">
            {loadingTx ? "Loading…" : "Pull from a past visit"}
          </button>
        </div>
        {picking ? (
          <div className="rounded-[12px] border border-[#E9E9EC] bg-white mb-2 overflow-hidden" data-testid="ps-preview-visit-list">
            {visits == null ? (
              <div className="px-4 py-3 text-[11.5px] text-[var(--gray2)]">Loading prior visits…</div>
            ) : visits.length === 0 ? (
              <div className="px-4 py-3 text-[11.5px] text-[var(--gray2)]">No prior visits with a transcript. Leave blank to use a built-in sample.</div>
            ) : visits.map((v, i) => (
              <button key={`${v.kind}-${v.id}`} onClick={() => pickVisit(v)} data-testid={`ps-preview-visit-${v.kind}-${v.id}`}
                className={`w-full text-left px-4 py-2.5 hover:bg-black/[.03] ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <div className="text-[11.5px] font-semibold text-[var(--ink)]">{v.patient_name || "Patient"} <span className="text-[10px] font-medium text-[var(--gray2)]">· {v.label || v.kind}</span></div>
                <div className="text-[10px] text-[var(--gray3)] mt-0.5">{v.date_of_service || ""}</div>
              </button>
            ))}
          </div>
        ) : null}
        <textarea value={tx} onChange={(e) => setTx(e.target.value)} rows={3} placeholder="Paste a transcript, pull one from a past visit, or leave blank to use a built-in sample"
          className="lf-input w-full resize-y text-[12px]" data-testid="ps-preview-transcript" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={gen} disabled={busy} data-testid="ps-preview-generate" className="btn-ink inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Eye size={14} />} Generate preview
        </button>
        {mode === "full" ? <button onClick={() => onOpenDq?.(13)} className="text-[11px] font-semibold text-[#4646b8]">DQ-13</button> : null}
      </div>

      {out ? (
        <div className="rounded-[16px] border border-[#E9E9EC] bg-white p-5" data-testid="ps-preview-out">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[9.5px] font-bold uppercase tracking-[.1em]" style={{ color: "#5856D6" }}>Written by revQ · not saved</div>
            {structured ? (
              <div className="flex items-center gap-1 text-[10.5px] font-semibold">
                <button onClick={() => setView("note")} data-testid="ps-preview-view-note" className={`px-2 py-0.5 rounded-full ${view === "note" ? "bg-[var(--ink)] text-white" : "text-[var(--gray2)]"}`}>Formatted note</button>
                <button onClick={() => setView("structured")} data-testid="ps-preview-view-structured" className={`px-2 py-0.5 rounded-full ${view === "structured" ? "bg-[var(--ink)] text-white" : "text-[var(--gray2)]"}`}>Structured</button>
              </div>
            ) : null}
          </div>
          {view === "note" ? (
            <div className="text-[11.5px] text-[var(--ink)] whitespace-pre-wrap leading-relaxed font-mono" data-testid="ps-preview-formatted">{formatted || "No note produced."}</div>
          ) : (
            Object.entries(structured || {}).map(([k, v]) => (
              <div key={k} className="mb-3 last:mb-0">
                <div className="text-[11.5px] font-bold text-[var(--ink)] capitalize">{k.replace(/_/g, " ")}</div>
                <div className="text-[11.5px] text-[var(--gray1)] mt-0.5 whitespace-pre-wrap leading-relaxed">{typeof v === "string" ? v : JSON.stringify(v, null, 2)}</div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
